// apps/client/src/config/subscriptionTiers.ts
// Subscription tier configuration for Valutten feature gating

/**
 * Subscription Tiers
 *
 * Plus ($497/mo): Basic features for individual brokers
 * Pro ($797/mo): Advanced features for growing brokerages
 * Enterprise ($1997/mo): Full suite for large enterprises
 * Network (internal): LATERAL tier for the lmg-network Elevate pilot tenant.
 *   Not on the upgrade path — it grants ONLY network_benchmarks, and no other
 *   tier grants that feature. Never shown on the pricing page.
 */

export type SubscriptionTier = 'plus' | 'pro' | 'enterprise' | 'network';

/**
 * Feature identifiers for gating
 */
export type Feature =
  // Plus tier (all tiers have access)
  | 'main_dashboard'
  | 'commission_breakdown'
  | 'loan_list'
  | 'clawback_report'
  | 'discharged_loans'
  | 'settlement_age_report'
  | 'trail_run_off_report'
  // Pro tier (Pro and Enterprise)
  | 'office_dashboard'
  | 'trail_book_valuation'
  | 'trail_forecast'
  | 'trail_drop_off'
  | 'book_health'
  | 'rcti_cashflow'
  | 'customer_portfolio'
  | 'multi_entity_upload'
  | 'shared_trail_valuation'
  // Commission-integrity report, decoupled from the Broker Activity add-on
  // (2026-07-20). Pro+ by tier; broker_activity holders keep access via the
  // union gate in AppRoutes so nobody is stripped.
  | 'trail_leakage'
  // CRM/MyCRM integration capability. Not surfaced as a standalone UI gate;
  // present for type completeness + backend union parity (crm_integration).
  | 'crm_integration'
  // Enterprise tier only
  | 'succession_readiness'
  | 'referral_partners'
  | 'refinance_intelligence'
  | 'income_summary'
  | 'acquisition_analysis'
  | 'custom_reporting'
  | 'api_access'
  | 'ai_assistant'
  | 'broker_activity'
  | 'lead_capture'
  // Referral Partners + Rewards add-on ($149/mo, Pro+). The route gate stays the
  // strict `featureFlags.partner_network === true` flag (RequirePartnerNetworkAccess).
  | 'partner_network'
  | 'broker_loan_books'
  | 'client_care'
  | 'loyalty_program'
  // Network tier only (lateral, ring-fenced — Elevate/LMG benchmarking)
  | 'network_benchmarks'
  // SFG / Salestrekker Pipeline Tracker. Explicit founder grant only; no tier
  // grants it. Its data plane is entirely separate from the MyCRM-fed pipeline
  // and funnel surfaces, which are becoming the LMG add-on.
  | 'pipeline_tracker';

/**
 * Feature configuration with tier requirements and display info
 */
export interface FeatureConfig {
  name: string;
  description: string;
  requiredTier: SubscriptionTier;
  benefits: string[];
  disabled?: boolean;
  /** When set, feature is only available to these company IDs (trial/rollout gating) */
  allowedCompanyIds?: string[];
  /** Skip the tier fast path and ask the server entitlement SSOT. */
  grantMode?: 'tier' | 'explicit';
}

/**
 * Complete feature registry with tier requirements
 */
export const FEATURE_REGISTRY: Record<Feature, FeatureConfig> = {
  // Plus tier features (available to all)
  main_dashboard: {
    name: 'Main Dashboard',
    description: 'Company-wide commission overview and KPIs',
    requiredTier: 'plus',
    benefits: ['Real-time commission tracking', 'Monthly breakdown', 'Broker performance'],
  },
  commission_breakdown: {
    name: 'Commission Breakdown',
    description: 'Detailed commission analysis by broker and lender',
    requiredTier: 'plus',
    benefits: ['Monthly tables', 'Lender breakdown'],
  },
  loan_list: {
    name: 'Loan List',
    description: 'Complete loan portfolio view with search',
    requiredTier: 'plus',
    benefits: ['Search all loans', 'Filter by status', 'Payment history'],
  },
  clawback_report: {
    name: 'Clawback Report',
    description: 'Track commission clawbacks and at-risk loans',
    requiredTier: 'plus',
    benefits: ['Clawback tracking', 'Risk identification', 'Historical data'],
  },
  discharged_loans: {
    name: 'Discharged Loans',
    description: 'View discharged loans and trail impact',
    requiredTier: 'plus',
    benefits: ['Discharge tracking', 'Trail loss analysis', 'Trend reporting'],
  },
  settlement_age_report: {
    name: 'Settlement Age Report',
    description: 'Analyze settlement timing and delays',
    requiredTier: 'plus',
    benefits: ['Settlement tracking', 'Aging analysis', 'Performance metrics'],
  },
  trail_run_off_report: {
    name: 'Broker Trail Run-Off Report',
    description: 'Project future trail income decline',
    requiredTier: 'plus',
    benefits: ['Trail projections', 'Run-off modeling', 'Book analysis'],
  },

  // Pro tier features
  office_dashboard: {
    name: 'Office Dashboard',
    description: 'Individual office performance with multi-dimensional filtering',
    requiredTier: 'pro',
    benefits: [
      'Office-level KPIs',
      'Multi-loan book filtering',
      'Broker comparisons',
      'Performance bands',
    ],
  },
  trail_book_valuation: {
    name: 'Trail Book Valuation',
    description: 'Calculate the market value of your trail book',
    requiredTier: 'pro',
    benefits: [
      'Book valuation',
      'Multiplier analysis',
      'Shareable reports',
      'Trend tracking',
    ],
  },
  trail_forecast: {
    name: 'Trail Forecast',
    description: 'Project future trail income with advanced modeling',
    requiredTier: 'pro',
    benefits: [
      '12-month projections',
      'Scenario modeling',
      'Growth forecasting',
      'Decay analysis',
    ],
  },
  trail_drop_off: {
    name: 'Loan Trail Drop-Off',
    description: 'Analyze trail book attrition patterns',
    requiredTier: 'pro',
    benefits: [
      'Drop-off tracking',
      'Retention metrics',
      'Trend analysis',
      'Early warning alerts',
    ],
  },
  book_health: {
    name: 'Book Health',
    description: 'Comprehensive trail book health assessment',
    requiredTier: 'pro',
    benefits: [
      'Health score',
      'Risk indicators',
      'Portfolio balance',
      'Recommendations',
    ],
  },
  rcti_cashflow: {
    name: 'RCTI Cashflow',
    description: 'Track expected vs actual commission payments',
    requiredTier: 'pro',
    benefits: [
      'Payment tracking',
      'Variance analysis',
      'RCTI reconciliation',
      'Cash flow forecasting',
    ],
  },
  customer_portfolio: {
    name: 'Customer Portfolio',
    description: 'Deep dive into customer relationships and value',
    requiredTier: 'pro',
    benefits: [
      'Customer insights',
      'Lifetime value',
      'Referral tracking',
      'Cross-sell opportunities',
    ],
  },
  multi_entity_upload: {
    name: 'Multi-Entity Upload',
    description: 'Upload all entity files in a single ZIP',
    requiredTier: 'pro',
    benefits: [
      'Bulk uploading',
      'Auto-routing',
      'Entity tracking',
      'Run completion status',
    ],
  },
  shared_trail_valuation: {
    name: 'Shared Trail Valuation',
    description: 'Generate shareable trail valuation reports',
    requiredTier: 'pro',
    benefits: [
      'Shareable links',
      'Custom branding',
      'Access controls',
      'Due diligence support',
    ],
  },

  // Enterprise tier features
  succession_readiness: {
    // BETA, EXPLICIT GRANT ONLY — the Enterprise tier alone does NOT unlock this.
    // The report packages whole-of-book economics for an acquirer, so enabling it
    // is a per-tenant commercial decision rather than a side effect of a tier
    // upgrade. Authority is server-side: getSuccessionReadinessReport and
    // generateSuccessionShareToken both call
    // requireFeature(FeatureFlag.SUCCESSION_READINESS), which reads
    // companies/{id}.featureFlags.succession_readiness or an entitlements/config
    // grant. Runtime-grantable via Firestore with no code deploy, and NOT a
    // hardcoded allowedCompanyIds list — same mechanism as broker_activity below.
    // `requiredTier` stays 'enterprise' only so the upgrade prompt reads sensibly.
    name: 'Succession Readiness',
    description: 'Buyer-facing succession readiness report with shareable links',
    requiredTier: 'enterprise',
    benefits: [
      'Readiness score',
      '14-item checklist',
      'Shareable buyer reports',
      'Due diligence support',
    ],
  },
  referral_partners: {
    name: 'Referral Partners',
    description: 'Analytics view of referral partner activity and spend',
    requiredTier: 'enterprise',
    benefits: [
      'Partner analytics',
      'Spend tracking',
      'Inactive partner alerts',
      'Period filtering',
    ],
  },
  refinance_intelligence: {
    name: 'Client Retention',
    description: 'AI-powered refinance opportunity detection',
    requiredTier: 'enterprise',
    benefits: [
      'Refinance detection',
      'Conversion tracking',
      'Competitive intelligence',
      'Retention strategies',
    ],
  },
  income_summary: {
    name: 'Income Summary',
    description: 'Comprehensive income reporting with payroll integration',
    requiredTier: 'enterprise',
    disabled: true,
    benefits: [
      'Full income view',
      'Expense tracking',
      'Payroll integration',
      'Profitability analysis',
    ],
  },
  acquisition_analysis: {
    name: 'Acquisition Analysis',
    description: 'Evaluate trail books for acquisition',
    requiredTier: 'enterprise',
    benefits: [
      'Book valuation',
      'Risk assessment',
      'Due diligence',
      'ROI projections',
    ],
  },
  custom_reporting: {
    name: 'Custom Reporting',
    description: 'Build custom reports and dashboards',
    requiredTier: 'enterprise',
    benefits: [
      'Custom metrics',
      'Scheduled reports',
      'API integration',
    ],
  },
  api_access: {
    name: 'API Access',
    description: 'Full API access for integrations',
    requiredTier: 'enterprise',
    benefits: [
      'REST API access',
      'Webhook support',
      'CRM integration',
      'Custom workflows',
    ],
  },
  ai_assistant: {
    name: 'AI Assistant',
    description: 'AI-powered insights and natural language queries',
    requiredTier: 'enterprise',
    benefits: [
      'Natural language queries',
      'AI insights',
      'Trend analysis',
      'Proactive recommendations',
    ],
  },
  broker_loan_books: {
    name: 'Broker Loan Books',
    description: 'Cross-template loan book analysis per broker with office attribution',
    requiredTier: 'enterprise',
    benefits: [
      'Per-broker loan book breakdown',
      'Cross-template analysis',
      'Canonical office attribution',
      'Commission split by type',
    ],
  },
  client_care: {
    // Standard for Pro + Enterprise (tierHasFeature is rank-based, so Enterprise inherits
    // the Pro grant). Plus unlocks via the per-company `featureFlags.client_care === true`
    // override (additive grant, no code deploy).
    name: 'Client Care',
    description: 'Living client database with automated outreach reminders and a weekly call list',
    requiredTier: 'pro',
    benefits: [
      'Client roster derived from your loan book',
      'Settlement anniversary, fixed-rate roll-off, review + birthday reminders',
      'Weekly per-broker call list — never miss a touchpoint',
      'Manager-visible contact history + trail protection',
    ],
  },
  loyalty_program: {
    name: 'Loyalty Rewards',
    description: 'Consumer-facing loyalty portal where borrowers earn points from trail commissions',
    requiredTier: 'enterprise',
    allowedCompanyIds: ['entourage-1766030715599'],
    benefits: [
      'Client loyalty portal',
      'Points accrual from trail',
      'Gift card redemptions',
      'Client retention tool',
    ],
  },
  trail_leakage: {
    // Commission-integrity report, decoupled from the Broker Activity add-on
    // (2026-07-20). Pro+ by tier; the route gate is a UNION with broker_activity
    // so existing broker_activity tenants (e.g. Entourage) keep access.
    name: 'Trail Leakage',
    description: 'Detects trail commission leakage by cross-referencing settled deals against your CRM',
    requiredTier: 'pro',
    benefits: [
      'Trail leakage detection against settled deals',
      'Missed-trail alerts per adviser',
      'Commission integrity assurance',
      'Recover revenue that would otherwise slip through',
    ],
  },
  crm_integration: {
    // Capability, not a sold add-on. Never rendered as a standalone FeatureGate;
    // present only so Record<Feature, FeatureConfig> stays exhaustive. Backend
    // union (requireCrmIntegration) is the real gate.
    name: 'CRM Integration',
    description: 'Connect your MyCRM / Loan Market account for CRM-driven analysis',
    requiredTier: 'enterprise',
    grantMode: 'explicit',
    benefits: [
      'MyCRM / Loan Market credential connection',
      'Daily deal-pipeline sync',
      'CRM-backed client enrichment',
    ],
  },
  broker_activity: {
    // Gated via CompanyEntitlements.promotionalFeatures (beta) or addOnFeatures (paid) —
    // NOT via a hardcoded allowedCompanyIds list. Any subscriber can be granted access
    // at runtime via Firestore without a code deploy. See
    // docs/runbooks/BROKER-ACTIVITY-BETA-GRANT.md for the grant mechanism.
    name: 'Team Activity',
    description: 'CRM integration, trail leakage detection, and team productivity tracking',
    requiredTier: 'enterprise',
    benefits: [
      'Trail leakage detection against MyCRM settled deals',
      'Daily CRM sync with deal pipeline snapshot',
      'Settled-deal cross-reference with raw commissions',
      'Google Chat alerts for missed trail + sync failures',
    ],
  },
  lead_capture: {
    // Separate paid add-on from broker_activity. Gated via
    // CompanyEntitlements.addOnFeatures.lead_capture (paid) or promotionalFeatures
    // (beta). Runtime grant via Firestore — no hardcoded allowedCompanyIds.
    // Standalone subapp at /leads/* with its own contextual top nav.
    name: 'Lead Capture',
    description: 'Multi-channel lead capture, attribution, and email automation',
    requiredTier: 'enterprise',
    benefits: [
      'Direct webhooks from WordPress, Meta Lead Ads, Google Ads (no Zapier)',
      'UTM + campaign attribution from first touch to settlement',
      'Cost per lead and ROAS per channel',
      'Email sequences via Resend (SPAM Act compliant suppression)',
      'Round-robin broker assignment per office',
    ],
  },
  partner_network: {
    // Referral Partners + Rewards. A paid add-on held as
    // entitlements/config.addOnFeatures.partner_network (Stripe or contract),
    // with `featureFlags.partner_network` as the tenant switch the client route
    // gate reads. `grantMode: 'explicit'`: no tier implies it.
    name: 'Referral Partners + Rewards',
    description: 'Refer clients to referral partners, track the fees owed to your brokers, pay them as gift cards',
    requiredTier: 'pro',
    grantMode: 'explicit',
    benefits: [
      'Refer a client to a partner in two minutes, with consent and fee disclosure recorded',
      'Every referral fee owed to your brokers, by stage, in one ledger',
      'Brokers redeem fees as Visa or everyday gift cards from their Rewards tab',
      'Back book radar: clients whose next move is a referral',
    ],
  },
  network_benchmarks: {
    // Elevate (LMG network benchmarking). requiredTier 'network' is LATERAL:
    // tierHasFeature grants it ONLY to the 'network' tier — Plus/Pro/Enterprise
    // never inherit it, and the 'network' tier inherits nothing else. The
    // authority is server-side regardless: computeNetworkBenchmarkEdition calls
    // requireFeature(companyId, FeatureFlag.NETWORK_BENCHMARKS, uid). Flag is
    // default OFF everywhere; no tenant holds it until the lmg-network pilot
    // tenant is created.
    name: 'Network Benchmarking',
    description: 'Elevate: LMG network benchmark editions with cohort percentiles',
    requiredTier: 'network',
    benefits: [
      'FY benchmark editions across the LMG network',
      'Cohort percentiles (size band, state, business model)',
      'Suppression-at-write confidentiality (n≥5 + dominance)',
      'One agreed formula per KPI via the network metric registry',
    ],
  },
  pipeline_tracker: {
    // SFG / Salestrekker Pipeline Tracker. `grantMode: 'explicit'` skips the
    // tier fast path and asks the server entitlement SSOT, because no tier
    // grants this: it is held as an addOnFeatures record with
    // grantSource 'contract'. requiredTier 'pro' is the FLOOR, not the grant.
    // Evoke is Pro, so a floor of 'enterprise' (as broker_activity and
    // lead_capture use) would have locked out the origin practice.
    name: 'Pipeline Tracker',
    description: 'Salestrekker pipeline and task reporting from weekly exports',
    requiredTier: 'pro',
    grantMode: 'explicit',
    benefits: [
      'Deals in flight by stage, with exact age in stage',
      'Stale and critical buckets against per-stage thresholds',
      'Task completion, overdue and outstanding per person',
      'Categorised loss reasons',
      'Weekly export upload with validation and freshness alerts',
    ],
  },
};

/**
 * Tier hierarchy for comparison
 */
const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  plus: 1,
  pro: 2,
  enterprise: 3,
  // Lateral tier — rank 0 is deliberate but NEVER compared: tierHasFeature
  // short-circuits 'network' out of the rank comparison in both directions.
  network: 0,
};

/**
 * Check if a tier has access to a feature.
 *
 * 'network' is a LATERAL tier, outside the Plus→Pro→Enterprise upgrade
 * hierarchy: a feature requiring 'network' is granted to the 'network' tier
 * ONLY (rank comparison would grant it to every tier), and the 'network' tier
 * is granted ONLY 'network'-required features (rank comparison would inherit
 * none or all depending on rank — both wrong).
 */
export function tierHasFeature(tier: SubscriptionTier, feature: Feature): boolean {
  const requiredTier = FEATURE_REGISTRY[feature]?.requiredTier;
  if (!requiredTier) return false;

  if (requiredTier === 'network' || tier === 'network') {
    return tier === requiredTier;
  }

  return TIER_HIERARCHY[tier] >= TIER_HIERARCHY[requiredTier];
}

export function featureRequiresExplicitGrant(feature: Feature): boolean {
  return FEATURE_REGISTRY[feature]?.grantMode === 'explicit';
}

/**
 * Get the upgrade tier for a feature
 */
export function getUpgradeTier(feature: Feature): SubscriptionTier {
  return FEATURE_REGISTRY[feature]?.requiredTier || 'pro';
}

/**
 * Check if a feature is enabled (not parked/disabled)
 */
export function isFeatureEnabled(feature: Feature): boolean {
  return !FEATURE_REGISTRY[feature]?.disabled;
}

/**
 * Get display name for a tier
 */
export function getTierDisplayName(tier: SubscriptionTier): string {
  const names: Record<SubscriptionTier, string> = {
    plus: 'Plus',
    pro: 'Pro',
    enterprise: 'Enterprise',
    network: 'Network',
  };
  return names[tier] || tier;
}

/**
 * Get tier pricing
 */
export function getTierPricing(tier: SubscriptionTier): string {
  const pricing: Record<SubscriptionTier, string> = {
    plus: '$497/mo',
    pro: '$797/mo',
    enterprise: '$1,997/mo',
    network: 'Internal', // non-billed pilot tier — never rendered on pricing surfaces
  };
  return pricing[tier];
}
